import { Component } from 'react';

import { isSameDay } from 'date-fns/isSameDay';

import BpkInput, {
  INPUT_TYPES,
} from '@skyscanner/backpack-web/bpk-component-input';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import ApplyFooter from './ApplyFooter';

import type { I18nService } from '@shared/types/i18n';
import type { Maybe } from '@shared/types/utils';

import STYLES from './ScrollableDateRangeSelector.module.scss';

import ScrollableRangeCalendar from '@shared/ui/components/ScrollRangeCalendar/ScrollableRangeCalendar';
import { withI18n } from '@shared/ui/contexts/i18n';
import { DATE_FORMAT } from '@shared/utils-universal/i18n/date-formats';

type Props = {
  i18n: I18nService;
  onApply: Function;
  onClose: Function;
  startDate: Maybe<Date>;
  endDate?: Maybe<Date>;
};

type State = {
  startDate: Maybe<Date>;
  endDate: Maybe<Date>;
};

const defaultProps = {
  endDate: null,
};

const cls = cssModules(STYLES);

class ScrollableDateRangeSelector extends Component<Props, State> {
  static defaultProps = defaultProps;

  constructor(props: Props) {
    super(props);
    const { endDate, startDate } = props;
    this.state = {
      startDate,
      endDate,
    };
  }

  onSubmit = () => {
    const { onApply, onClose } = this.props;
    const { endDate, startDate } = this.state;

    /*
       Inside onApply, we call window.history.pushState to update the url with new selected date;
       Inside onClose, we call react-router history.push to navigate to the previous page.
       References:
       - pushNewUrl() in apps/client/src/services/url/url.ts
       - apps/client/src/components/HashRoute/HashRoute.tsx

       Any native window.history.pushState must be called before react-router history.push,
       otherwise the url will not be updated correctly.
       The exact mechanism underlying this behavior is unclear,
       but it appears related to how react-router manages the browser history stack.

       So here we call onApply before onClose.
    */
    onApply(startDate, endDate);
    onClose();
  };

  onDateRangeChange = (startDate: Date, endDate: Date) => {
    this.setState({ startDate, endDate });
  };

  render() {
    const { endDate, startDate } = this.state;
    const {
      i18n: { formatDate: formatDateI18n, translate },
    } = this.props;

    return (
      <div className={cls('ScrollableDateRangeSelector')}>
        <div className={cls('ScrollableDateRangeSelector__content')}>
          <div className={cls('ScrollableDateRangeSelector__dateRange')}>
            <div
              className={cls(
                'ScrollableDateRangeSelector__date',
                'ScrollableDateRangeSelector__date--checkIn',
              )}
            >
              <BpkText textStyle={TEXT_STYLES.label2} tagName="p">
                {translate('SelectionSummary_label_checkIn')}
              </BpkText>
              <BpkInput
                id="scrollableDateCheckInId"
                type={INPUT_TYPES.text}
                name="scrollableDateCheckInId"
                className={cls('ScrollableDateRangeSelector__date--input')}
                value={
                  startDate
                    ? formatDateI18n(
                        startDate,
                        DATE_FORMAT.MONTH_DAY_ABBR,
                      ).replace(',', '')
                    : translate('HotelStay_label_checkIn')
                }
                placeholder={translate('HotelStay_label_checkIn')}
                readOnly
              />
            </div>
            <div
              className={cls(
                'ScrollableDateRangeSelector__date',
                'ScrollableDateRangeSelector__date--checkOut',
              )}
            >
              <BpkText textStyle={TEXT_STYLES.label2} tagName="p">
                {translate('CheckoutForm_label_checkOut')}
              </BpkText>
              <BpkInput
                id="scrollableDateCheckOutId"
                type={INPUT_TYPES.text}
                name="scrollableDateCheckOutId"
                className={cls('ScrollableDateRangeSelector__date--input')}
                value={
                  endDate
                    ? formatDateI18n(
                        endDate,
                        DATE_FORMAT.MONTH_DAY_ABBR,
                      ).replace(',', '')
                    : translate('HotelStay_label_checkOut')
                }
                placeholder={translate('HotelStay_label_checkOut')}
                readOnly
                disabled={!endDate}
              />
            </div>
          </div>
          <ScrollableRangeCalendar
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={this.onDateRangeChange}
          />
          <ApplyFooter
            checkIn={startDate}
            checkOut={endDate}
            disabled={
              !startDate ||
              !endDate ||
              isSameDay(startDate, endDate) ||
              (startDate === this.props.startDate &&
                endDate === this.props.endDate)
            }
            onApply={this.onSubmit}
          />
        </div>
      </div>
    );
  }
}

export default withI18n(ScrollableDateRangeSelector);
